# Componente: `SearchableSelect` Este componente gestiona la selección de
opciones en un desplegable interactivo con capacidad de búsqueda. Es una
alternativa mejorada al `select` nativo de HTML, ideal para listas extensas
donde la búsqueda facilita la experiencia del usuario. * **Búsqueda y
Filtrado:** * Permite al usuario escribir en el campo de entrada para filtrar
dinámicamente las opciones disponibles. * Soporta búsqueda por propiedades
específicas de objetos (`filterKeys`) o en todas las propiedades si no se
especifican. * **Propiedades (`props`):** * `options` (Array, requerido): El
conjunto de datos (valores primitivos u objetos) a mostrar en la lista. *
`selected` (Tipo variado): El valor de la opción actualmente seleccionada,
utilizado para inicializar y controlar el estado. * `placeholderKey` (String,
por defecto `'title'`): La clave del objeto a mostrar como texto de la opción si
`options` son objetos. * `defaultPlaceholder` (String): El texto predeterminado
del campo cuando no hay selección. * `useLastAsPlaceholder` (Boolean, por
defecto `false`): Si es `true`, la opción seleccionada se mostrará como
placeholder. * `filterKeys` (Array, por defecto `[]`): Array de claves de objeto
para usar en la función de búsqueda. * `disabledItems` (Array, por defecto
`[]`): Opciones específicas que se mostrarán pero no podrán ser seleccionadas. *
`isDisabledComponent` (Boolean, por defecto `false`): Deshabilita completamente
el componente, impidiendo cualquier interacción. * **Eventos Emisivos
(`emits`):** * `select`: Emitido cuando el usuario selecciona una opción. La
carga útil contiene la opción seleccionada. * `dropdown-opened`: Emitido cada
vez que el menú desplegable se abre, útil para disparar acciones en el
componente padre (ej., scroll automático). */

<template>
  <div
    v-if="hasOptionsToShow || searchFunction"
    class="custom-select-container"
  >
    <div style="position: relative; width: 100%">
      <div
        v-if="
          computedPlaceholder !== defaultPlaceholder &&
          $slots.selected &&
          !inputFocused
        "
        class="inputMuniV2 BoldLetter"
        @click="handleFocusAndSearch"
        tabindex="0"
        @blur="hideDropdownWithDelay"
      >
        <slot name="selected" :selected="selected" />
      </div>

      <input
        v-model="searchQuery"
        @keydown="handleKeydown"
        type="text"
        :placeholder="computedPlaceholder"
        class="inputMuniV2"
        @focus="handleFocus"
        ref="searchInput"
        @blur="hideDropdownWithDelay"
        :class="{
          BoldLetter: hasSelection,
          InvisibleI:
            computedPlaceholder !== defaultPlaceholder &&
            $slots.selected &&
            !inputFocused,
        }"
        :disabled="isDisabledComp"
      />

      <ul
        v-if="showDropdown && shouldShowDropdown && !isDisabledComp"
        class="dropdown-list"
        :class="{
          'dropdown-list--overlay': overlayDropdown,
          'dropdown-list--upwards': overlayDropdown && openUpwards,
        }"
      >
        <li
          v-if="isLoading"
          class="listElement"
          style="
            cursor: default;
            justify-content: center;
            color: var(--text-primary);
          "
        >
          Cargando...
        </li>

        <template v-else>
          <li
            v-for="(option, index) in filteredOptions"
            :key="index"
            :ref="setOptionRef(index)"
            @mousedown.prevent="selectOption(option)"
            :class="[
              'listElement',
              'BoldLetter',
              {
                'listElement--selected': isSelected(option),
                'listElement--disabled': isDisabled(option),
                'listElement--active': index === activeIndex,
              },
            ]"
          >
            <slot :option="option" :index="index">
              {{ displayValue(option) }}
            </slot>
          </li>
        </template>

        <li
          v-if="!isLoading && filteredOptions.length === 0"
          class="listElement BoldLetter"
          style="padding: 8px; color: #999; justify-content: center"
        >
          No hay coincidencias
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick } from "vue";

export default {
  name: "SearchableSelect",
  props: {
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
    // Función asíncrona para buscar (opcional)
    searchFunction: {
      type: Function,
      default: null,
    },
    // Tiempo de espera para ejecutar la búsqueda (debounce)
    debounceDelay: {
      type: Number,
      default: 400,
    },
    disabledItems: {
      type: Array,
      default: () => [],
    },
    selected: {
      type: [Object, String, Number, Array],
      default: null,
    },
    useLastAsPlaceholder: {
      type: Boolean,
      default: false,
    },
    placeholderKey: {
      type: [String, Array],
      default: "title",
    },
    displayKeys: {
      type: [String, Array],
      default: null,
    },
    defaultPlaceholder: {
      type: String,
      default: "Selecciona una opción",
    },
    filterKeys: {
      type: Array,
      default: () => [],
    },
    headerText: {
      type: String,
      default: "OPCIONES",
    },
    isDisabledComponent: {
      type: Boolean,
      default: false,
    },
    overlayDropdown: {
      type: Boolean,
      default: false,
    },
    openUpwards: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["select", "dropdown-opened"],

  setup(props, { emit }) {
    const showDropdown = ref(false);
    const searchInput = ref(null);
    const searchQuery = ref("");
    const internalSelected = ref(props.selected);
    const warnedKeys = new Set();
    const inputFocused = ref(false);
    const suppressNextHide = ref(false);
    const activeIndex = ref(-1);
    const optionRefs = ref([]);

    // --- NUEVOS REFS PARA ASYNC ---
    const isLoading = ref(false);
    const asyncOptions = ref([]);
    let debounceTimer = null;

    // Inicializar asyncOptions con las options que vengan por prop al principio
    watch(
      () => props.options,
      (newVal) => {
        if (props.searchFunction) {
          asyncOptions.value = newVal;
        }
      },
      { immediate: true },
    );

    // Sincronizar selección y placeholder
    watch(
      () => props.selected,
      (newVal) => {
        if (JSON.stringify(newVal) !== JSON.stringify(internalSelected.value)) {
          internalSelected.value = newVal;
          if (newVal && searchQuery.value === "") {
            searchQuery.value = getDisplayValueForPlaceholder(newVal);
          } else if (!newVal && searchQuery.value !== "") {
            searchQuery.value = "";
          }
        }
      },
      { immediate: true },
    );

    // --- LÓGICA DE BÚSQUEDA (HÍBRIDA: LOCAL O ASÍNCRONA) ---
    watch(searchQuery, (newQuery) => {
      // Si no hay función asíncrona, no hacemos nada aquí (el computed filteredOptions se encarga)
      if (!props.searchFunction) return;

      // Si el query coincide con lo seleccionado actualmente (placeholder visual), no disparamos búsqueda
      if (
        internalSelected.value &&
        newQuery === getDisplayValueForPlaceholder(internalSelected.value)
      ) {
        return;
      }

      // Limpiar timer anterior
      if (debounceTimer) clearTimeout(debounceTimer);

      // Si borró todo, volvemos a mostrar las opciones por defecto
      if (!newQuery || !newQuery.trim()) {
        asyncOptions.value = props.options;
        isLoading.value = false;
        return;
      }

      isLoading.value = true;

      // Iniciar Debounce
      debounceTimer = setTimeout(async () => {
        try {
          const results = await props.searchFunction(newQuery);
          asyncOptions.value = Array.isArray(results) ? results : [];
        } catch (error) {
          console.error("[SearchableSelect] Error buscando:", error);
          asyncOptions.value = [];
        } finally {
          isLoading.value = false;
        }
      }, props.debounceDelay ?? 100);
    });

    const filteredOptions = computed(() => {
      if (props.searchFunction) {
        return asyncOptions.value;
      }

      if (!searchQuery.value) return props.options;

      const rawQuery = searchQuery.value.toString().toLowerCase().trim();
      const searchTerms = rawQuery
        .split(/\s+/)
        .filter((term) => term.length > 0);

      if (searchTerms.length === 0) return props.options;

      return props.options.filter((option) => {
        // --- CASO 1: Array de primitivos (Strings, Numbers) ---
        if (typeof option !== "object" || option === null) {
          if (option == null) return false;
          const optionStr = option.toString().toLowerCase();

          // Verificamos que TODAS las palabras buscadas estén en el string
          return searchTerms.every((term) => optionStr.includes(term));
        }

        // --- CASO 2: Array de objetos ---
        const keysToFilter =
          props.filterKeys.length > 0 ? props.filterKeys : Object.keys(option);

        const searchableText = keysToFilter
          .map((key) => {
            if (!Object.prototype.hasOwnProperty.call(option, key)) {
              if (!warnedKeys.has(key)) {
                console.warn(
                  `[SearchableSelect] La clave "${key}" no existe en el objeto para el filtro:`,
                  option,
                );
                warnedKeys.add(key);
              }
              return "";
            }

            const value = option[key];
            return value != null ? value.toString().toLowerCase() : "";
          })
          .join(" "); // Unimos con espacio para evitar pegotes accidentales

        // Verificamos que TODOS los términos de búsqueda existan en el texto concatenado
        return searchTerms.every((term) => searchableText.includes(term));
      });
    });

    const setOptionRef = (index) => (el) => {
      if (!el) return;
      optionRefs.value[index] = el;
    };

    watch(filteredOptions, () => {
      optionRefs.value = [];
    });

    watch(activeIndex, (newIndex) => {
      nextTick(() => {
        const el = optionRefs.value[newIndex];
        if (!el) return;

        el.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      });
    });

    const handleKeydown = (e) => {
      const options = filteredOptions.value;
      console.log("activeIndex:", activeIndex.value);
      if (!showDropdown.value) {
        showDropdown.value = true;
        activeIndex.value = options.findIndex((opt) => !isDisabled(opt));
        return;
      }

      const moveDown = () => {
        let next = activeIndex.value;
        do {
          next++;
        } while (next < options.length && isDisabled(options[next]));

        if (next < options.length) activeIndex.value = next;
      };

      const moveUp = () => {
        let prev = activeIndex.value;
        do {
          prev--;
        } while (prev >= 0 && isDisabled(options[prev]));

        if (prev >= 0) activeIndex.value = prev;
      };

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (activeIndex.value === -1) {
            activeIndex.value = options.findIndex((opt) => !isDisabled(opt));
          } else {
            moveDown();
          }
          break;

        case "ArrowUp":
          e.preventDefault();
          moveUp();
          break;

        case "Enter":
          e.preventDefault();
          if (
            activeIndex.value >= 0 &&
            options[activeIndex.value] &&
            !isDisabled(options[activeIndex.value])
          ) {
            selectOption(options[activeIndex.value]);
          }
          break;

        case "Escape":
          showDropdown.value = false;
          activeIndex.value = -1;
          break;
      }
    };

    watch(searchQuery, () => {
      activeIndex.value = -1;
    });

    // Determina si una opción está deshabilitada
    const isDisabled = (option) => {
      if (!props.disabledItems || props.disabledItems.length === 0)
        return false;
      return props.disabledItems.some((item) => {
        if (
          typeof option === "object" &&
          typeof item === "object" &&
          option !== null &&
          item !== null
        ) {
          if ("id" in option && "id" in item) {
            return option.id === item.id;
          }
          return JSON.stringify(item) === JSON.stringify(option);
        }
        return item === option;
      });
    };

    // Determina si una opción está seleccionada
    const isSelected = (option) => {
      if (
        !props.selected ||
        (Array.isArray(props.selected) && props.selected.length === 0)
      ) {
        return false;
      }

      if (Array.isArray(props.selected)) {
        return props.selected.some((selectedItem) => {
          if (
            typeof option === "object" &&
            typeof selectedItem === "object" &&
            option !== null &&
            selectedItem !== null
          ) {
            if ("id" in option && "id" in selectedItem) {
              return option.id === selectedItem.id;
            }
            return JSON.stringify(option) === JSON.stringify(selectedItem);
          }
          return option === selectedItem;
        });
      } else {
        if (
          typeof option === "object" &&
          typeof props.selected === "object" &&
          option !== null &&
          props.selected !== null
        ) {
          if ("id" in option && "id" in props.selected) {
            return option.id === props.selected.id;
          }
          return JSON.stringify(option) === JSON.stringify(props.selected);
        }
        return option === props.selected;
      }
    };

    // Maneja la selección
    const selectOption = (option) => {
      if (props.isDisabledComponent || isDisabled(option)) return;
      internalSelected.value = option;
      emit("select", option);

      if (!props.useLastAsPlaceholder) {
        searchQuery.value = "";
        // Al seleccionar y limpiar, si es async, restauramos la lista original
        if (props.searchFunction) {
          asyncOptions.value = props.options;
        }
      } else {
        searchQuery.value = getDisplayValueForPlaceholder(option);
      }

      showDropdown.value = false;
      inputFocused.value = false;
      if (searchInput.value) searchInput.value.blur();
    };

    // Manejo de foco
    const handleFocus = async () => {
      if (props.isDisabledComponent) {
        showDropdown.value = false;
        return;
      }
      inputFocused.value = true;
      if (!showDropdown.value) {
        showDropdown.value = true;
        await nextTick();
        emit("dropdown-opened");
      }
      if (internalSelected.value) {
        searchQuery.value = "";
      }
      activeIndex.value = filteredOptions.value.findIndex(
        (opt) => !isDisabled(opt),
      );
    };

    const handleFocusAndSearch = async () => {
      suppressNextHide.value = true;
      inputFocused.value = true;
      await handleFocus();
      searchQuery.value = "";
      await nextTick();
      searchInput.value?.focus();
      setTimeout(() => {
        suppressNextHide.value = false;
      }, 300);
    };

    const hideDropdownWithDelay = () => {
      if (suppressNextHide.value) return;
      setTimeout(() => {
        showDropdown.value = false;
        inputFocused.value = false;

        if (
          props.useLastAsPlaceholder &&
          internalSelected.value &&
          searchQuery.value === ""
        ) {
          searchQuery.value = getDisplayValueForPlaceholder(
            internalSelected.value,
          );
        } else if (!props.useLastAsPlaceholder && searchQuery.value !== "") {
          searchQuery.value = "";
        }
      }, 200);
    };

    // Obtener valor para mostrar en la lista
    const displayValue = (option) => {
      if (typeof option !== "object" || option === null) {
        return option;
      }

      const keysToUse = props.displayKeys || props.placeholderKey;

      if (Array.isArray(keysToUse)) {
        const parts = keysToUse
          .map((key) => {
            if (Object.prototype.hasOwnProperty.call(option, key)) {
              return option[key] != null ? String(option[key]) : "";
            } else {
              if (!warnedKeys.has(key)) {
                console.warn(
                  `[SearchableSelect] La clave "${key}" no existe en el objeto para mostrar:`,
                  option,
                );
                warnedKeys.add(key);
              }
              return "";
            }
          })
          .filter(Boolean);

        return parts.length > 0 ? parts.join(" ") : "Sin título";
      } else {
        if (Object.prototype.hasOwnProperty.call(option, keysToUse)) {
          return option[keysToUse] != null
            ? String(option[keysToUse])
            : "Sin título";
        } else {
          if (!warnedKeys.has(keysToUse)) {
            console.warn(
              `[SearchableSelect] La clave "${keysToUse}" no existe en el objeto para mostrar:`,
              option,
            );
            warnedKeys.add(keysToUse);
          }
          return "Sin título";
        }
      }
    };

    // Obtener valor para el placeholder (input)
    const getDisplayValueForPlaceholder = (option) => {
      if (typeof option !== "object" || option === null) {
        return option;
      }

      const keysToUse = props.placeholderKey;

      if (Array.isArray(keysToUse)) {
        const parts = keysToUse
          .map((key) => {
            if (Object.prototype.hasOwnProperty.call(option, key)) {
              return option[key] != null ? String(option[key]) : "";
            }
            return "";
          })
          .filter(Boolean);

        return parts.length > 0 ? parts.join(" ") : "";
      } else {
        return option[keysToUse] != null ? String(option[keysToUse]) : "";
      }
    };

    const computedPlaceholder = computed(() => {
      if (props?.useLastAsPlaceholder && internalSelected.value) {
        return getDisplayValueForPlaceholder(internalSelected.value);
      }
      return props.defaultPlaceholder;
    });

    const hasSelection = computed(() => {
      return (
        !!internalSelected.value &&
        searchQuery.value ===
          getDisplayValueForPlaceholder(internalSelected.value)
      );
    });

    const hasOptionsToShow = computed(() => {
      // Muestra si hay opciones o si hay una función de búsqueda configurada
      return props.options?.length >= 0 || !!props.searchFunction;
    });

    const shouldShowDropdown = computed(() => {
      if (props.isDisabledComponent) return false;
      // Muestra si está cargando, si hay opciones filtradas, o si la lista está vacía (para mensaje "No hay coincidencias")
      return (
        isLoading.value ||
        filteredOptions.value.length > 0 ||
        filteredOptions.value.length === 0
      );
    });

    return {
      isLoading,
      hasOptionsToShow,
      shouldShowDropdown,
      showDropdown,
      searchInput,
      searchQuery,
      filteredOptions,
      computedPlaceholder,
      hasSelection,
      isDisabled,
      isSelected,
      selectOption,
      handleFocus,
      handleFocusAndSearch,
      hideDropdownWithDelay,
      displayValue,
      handleKeydown,
      activeIndex,
      setOptionRef,
      isDisabledComp: computed(() => props.isDisabledComponent),
      inputFocused,
    };
  },
};
</script>

<style scoped>
/* @import '@/components/Utils/css/form-styles.css'; */

.dropdown-list {
  /* Por defecto, si no es 'overlay', estará en el flujo normal */
  /* Puedes dejarlo sin posicionamiento específico o con position: static */
  /* Esto hará que ocupe espacio y desplace el contenido */

  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  max-height: 200px;
  overflow-y: auto;
  list-style: none;
  padding: 0;
  margin-top: 5px;
  /* Margen para que no pegue con el input */
  /* NO tendrá position: absolute ni z-index por defecto */

  /* Puedes agregar un ancho fijo o max-width si lo necesitas */
  width: 100%;
  /* Para que ocupe el ancho completo */
  box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.08);
  /* Sombra más sutil por defecto */
}

/* Estilos para cuando el dropdown DEBE SUPERPONERSE (no desplazar) */
.dropdown-list--overlay {
  position: absolute;
  /* CLAVE: lo saca del flujo normal */
  top: 100%;
  /* Por defecto: debajo del input */
  left: 0;
  right: 0;
  /* Asegura que ocupe todo el ancho del contenedor relativo */
  z-index: 1000;
  /* Lo pone por encima de otros elementos */
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  /* Sombra más pronunciada al superponerse */
}

/* Estilos para cuando el dropdown es overlay Y debe abrirse hacia arriba */
.dropdown-list--upwards {
  top: auto;
  /* Anula el 'top: 100%' */
  bottom: 100%;
  /* Se posiciona arriba del input */
  margin-top: 0;
  /* Anula el margen superior por defecto */
  margin-bottom: 5px;
  /* Espacio entre input y dropdown cuando abre hacia arriba */
}

.custom-select-container.is-disabled .label-text {
  color: #aaa;
}

/* Si quieres que todo el componente se vea atenuado cuando está deshabilitado */
.custom-select-container.is-disabled {
  pointer-events: none;
  /* Deshabilita interacciones de ratón en el contenedor */
}

/* Si tu form-styles.css ya tiene estilos para :disabled, estos pueden sobrescribirlos.
   Asegúrate de que tus estilos específicos aquí tengan mayor especificidad o se carguen después. */

.inputMuniV2 {
  width: 100%;
  padding: 5px 1.2rem;
  background: var(--border);
  height: 30px;
  /* box-shadow: none !important; */
  border-color: none;
  border: 0px;
  border-radius: 11px;
  font-size: 13px;
  text-align: start;

  line-height: normal;

  display: flex;

  align-items: center;
}

.inputMuniV2:disabled {
  background-color: var(--border);
}

.dropdown-list {
  /*list-style: none; 
    padding: 0; 
    margin: 0; 
    width: auto; 
    background: var(--bg-popup); 
    border: 1px solid #ccc; estos estilos son de lista comun
    max-height: 200px; 
    overflow-y: auto;*/
  list-style: none;
  padding: 0;
  gap: 0px;
  margin-top: 15px;
  width: 100%;
  background: none;
  max-height: 200px;
  overflow-y: auto;
  scrollbar-width: none;
  border-radius: 8px;
}

.listElement {
  color: var(--text-primary);
  height: 35px;
  display: flex;
  padding: 0 25.605px;
  background: var(--border);
  align-items: center;
  gap: 38.407px;
  cursor: pointer;
  font-size: 13px;
}

.listElement--selected {
  cursor: auto;

  background: #b2b2b2 !important;
  color: var(--text-primary);
}

.InvisibleI {
  position: absolute;
  visibility: hidden;
  height: 0px;
  width: 0px;
  padding: 0;
  margin: 0;
  border: none;
}
.listElement--active {
  background: #bfbfbf !important;
  height: 35px;
  border-radius: 4px;
}
</style>
